import { Suspense, lazy, useEffect, useState } from 'react'
import { NavLink, Route, Routes, useLocation } from 'react-router-dom'
import { EnvBadge } from './components/EnvBadge'
import { RequireAdmin, useAdminMe } from './components/RequireAdmin'
import { DEV_FLAG_EVENT, applyDevFlagFromQuery, isDevPanelOn } from './dev/devFlag'
import { can } from './lib/adminApi'
import { AboutPage } from './pages/AboutPage'
import { AdminCardEditPage } from './pages/AdminCardEditPage'
import { AdminDataPage } from './pages/AdminDataPage'
import { AdminDevPage } from './pages/AdminDevPage'
import { AdminLoginPage } from './pages/AdminLoginPage'
import { AdminMatchPage } from './pages/AdminMatchPage'
import { AdminMatchRunPage } from './pages/AdminMatchRunPage'
import { AdminMatchUnmetPage } from './pages/AdminMatchUnmetPage'
import { AdminWeightPage } from './pages/AdminWeightPage'
import { AdminOrgEditPage } from './pages/AdminOrgEditPage'
import { AdminOrgListPage } from './pages/AdminOrgListPage'
import { AdminProblemEditPage } from './pages/AdminProblemEditPage'
import { AdminMatchByCardPage } from './pages/AdminMatchByCardPage'
import { AdminNewsGuidePage } from './pages/AdminNewsGuidePage'
import { AdminReviewPage } from './pages/AdminReviewPage'
import { AdminSettingsPage } from './pages/AdminSettingsPage'
import { AdminCodesPage } from './pages/AdminCodesPage'
import { BoardDetailPage } from './pages/BoardDetailPage'
import { BoardListPage } from './pages/BoardListPage'
import { HomePage } from './pages/HomePage'
import { NotFoundPage } from './pages/NotFoundPage'
import { OrgDetailPage } from './pages/OrgDetailPage'
import { ProblemDetailPage } from './pages/ProblemDetailPage'
import { ProblemListPage } from './pages/ProblemListPage'
import { ProblemNewPage } from './pages/ProblemNewPage'
import { ProblemEditPage } from './pages/ProblemEditPage'
import { PrivacyPage } from './pages/PrivacyPage'
import { SitemapPage } from './pages/SitemapPage'
import { SolutionDetailPage } from './pages/SolutionDetailPage'
import { SolutionListPage } from './pages/SolutionListPage'

/**
 * **지도만 따로 내려받는다.** leaflet은 다른 화면이 전혀 쓰지 않는데 같이 묶이면
 * 홈·목록을 여는 사람도 지도 라이브러리를 받게 된다(합치면 첫 번들이 약 150kB 늘었다).
 * 라우트 단위 분리는 여기 하나면 충분하다 — 나머지 화면은 서로 코드를 나눠 쓴다.
 */
const MapPage = lazy(() => import('./pages/MapPage').then((m) => ({ default: m.MapPage })))

/**
 * dev 오버레이는 **빌드 시점에 들어갈지가 정해진다**(기획 §15-3, 2026-08-19).
 *
 * `false`면 아래 `import()`가 아예 만들어지지 않아 **청크 자체가 산출물에 없다** —
 * 운영 번들에 개발 도구가 없다는 것을 플래그가 아니라 빌드가 보장한다.
 * 그전에는 청크가 빌드되고 localStorage 플래그가 요청 여부만 정했다(§9).
 *
 * 값은 `vite.config.ts`의 `define`이 **불리언 리터럴로 박는다** — `import.meta.env.VITE_*`를
 * 그대로 쓰면 조건이 접히지 않아 청크가 남는다(그 실측이 vite.config 주석에 있다).
 * 켜지는 곳: `vite dev`(로컬) · `VITE_ENABLE_DEVPANEL=true`로 구운 프리뷰 빌드.
 * **운영 빌드는 켜지 않는다**(D-7) — 운영 확인의 본체는 `/admin/dev` 서버 화면이고,
 * 오버레이가 보여주는 것(요청 로그·콘솔 에러)은 개발자도구의 부분집합이다.
 *
 * 빌드에 들어와도 마운트는 브라우저별 localStorage 플래그가 한 번 더 가른다 —
 * FAB이 늘 떠 있으면 화면을 가리기 때문이다.
 */
declare const __DEV_PANEL_IN_BUILD__: boolean

const DEV_PANEL_IN_BUILD = __DEV_PANEL_IN_BUILD__

const DevOverlay = DEV_PANEL_IN_BUILD
  ? lazy(() => import('./dev/DevOverlay').then((m) => ({ default: m.DevOverlay })))
  : null

/** 플래그를 구독한다 — 오버레이의 '패널 끄기'가 같은 탭에서 즉시 반영돼야 한다. */
function useDevPanelFlag(): boolean {
  const [on, setOn] = useState(isDevPanelOn)
  useEffect(() => {
    const sync = () => setOn(isDevPanelOn())
    // 주소로 켜고 끄는 편의 경로. 빌드에 오버레이가 없으면 볼 필요도 없다.
    if (DEV_PANEL_IN_BUILD) {
      applyDevFlagFromQuery(window.location.search)
      sync()
    }
    window.addEventListener(DEV_FLAG_EVENT, sync)
    // 다른 탭에서 켜고 끈 것도 따라간다(storage는 다른 탭에서만 온다).
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener(DEV_FLAG_EVENT, sync)
      window.removeEventListener('storage', sync)
    }
  }, [])
  return on
}

/**
 * 공개 메뉴 — **2026-08-14 BI·브랜드 가이드의 헤더 메뉴안**을 따른다
 * (`소개 · 시민문제카드 · 기업해결카드 · 이야기 · 소식`, 로고가 홈).
 * 이름은 시안 표기를 그대로 쓴다 — `시민 문제`가 아니라 `시민문제카드`다.
 *
 * **해봄지도는 시안에 없지만 넣는다.** 과업지시서 §3·§4가 요구하는 핵심 기능이라 뺄 수 없다.
 * 시안은 디자인 시안이지 확정 IA가 아닌 것으로 보고, 카드 2종 바로 뒤에 둔다 —
 * 같은 자료를 목록으로 보느냐 지도로 보느냐의 차이라 그 자리가 자연스럽다.
 * 이티랩에 되묻는 중이며(§40_전달/2026-08-14) 회신이 오면 이 배열부터 고친다.
 *
 * **`이야기`·`소식`은 게시판 2종이다.** 별도 화면이 아니라 `/board`를 `board` 파라미터로 가른다 —
 * 게시판 종류는 `common_code`에서 오므로 화면·라우트를 종류마다 만들지 않는다(BoardListPage 주석).
 * 그래서 여기서만 코드값을 안다. 종류가 늘어도 목록 화면은 그대로 받고, 메뉴에 올릴지만 정하면 된다.
 */
type MenuItem = { to: string; label: string; end: boolean; board?: string }

const MENU: MenuItem[] = [
  { to: '/about', label: '소개', end: true },
  { to: '/problems', label: '시민문제카드', end: false },
  { to: '/solutions', label: '기업해결카드', end: false },
  { to: '/map', label: '해봄지도', end: false },
  { to: '/board?board=org_visit', label: '이야기', end: false, board: 'org_visit' },
  { to: '/board?board=notice', label: '소식', end: false, board: 'notice' },
]

/**
 * 관리자 메뉴 — 검수와 소식 관리는 화면이 완전히 다르므로 나눠 둔다.
 * '기업 관리'는 **카드를 만드는** 곳이고 '카드 검수'는 **만들어진 카드를 공개할지 판단하는** 곳이다.
 */
const ADMIN_MENU = [
  { to: '/admin', label: '카드 검수', end: true },
  { to: '/admin/orgs', label: '기업 관리', end: false },
  { to: '/admin/match', label: '매칭', end: false },
  // 이름을 하는 일에 맞춘다 — 「데이터 관리」는 기준 데이터를 손보는 자리로 읽히는데
  // 실제로는 조건으로 걸러 CSV·엑셀로 받는 화면이다(코드값 편집은 설정 하위에 있다).
  { to: '/admin/data', label: '데이터 내보내기', end: false },
  { to: '/admin/posts', label: '소식 게시판', end: false },
  { to: '/admin/settings', label: '설정', end: false },
]

/**
 * 관리자 내비게이션을 컴포넌트로 가른 이유: `useAdminMe`가 여기서만 돌게 하기 위해서다.
 * `App` 본문에서 부르면 **공개 방문자 전원이 401 요청을 한 번씩 쏘게 된다** —
 * 관리자 화면(isAdmin)일 때만 마운트되므로 그 요청이 생기지 않는다.
 */
function AdminNav() {
  const me = useAdminMe()
  return (
    <>
      {ADMIN_MENU.map((m) => (
        <NavLink
          key={m.to}
          to={m.to}
          end={m.end}
          className={({ isActive }) => (isActive ? 'active' : undefined)}
        >
          {m.label}
        </NavLink>
      ))}
      {/* dev 패널 메뉴 — 권한(dev:access)이 있는 세션에만 보인다. 게이트·권한의 실제
          차단은 서버가 하고(admin-dev.ts), 여기는 안 보이게만 한다. */}
      {can(me.data, 'dev', 'access') && (
        <NavLink to="/admin/dev" className={({ isActive }) => (isActive ? 'active' : undefined)}>
          개발자
        </NavLink>
      )}
      <NavLink to="/">공개 사이트</NavLink>
    </>
  )
}

export function App() {
  const location = useLocation()
  const devPanelOn = useDevPanelFlag()
  // 관리자 화면은 공개 메뉴를 쓰지 않는다 — 공개 메뉴는 시민·기업용 체계다.
  const isAdmin = location.pathname === '/admin' || location.pathname.startsWith('/admin/')

  /**
   * 게시판 메뉴 2개는 pathname이 `/board`로 같고 `board` 파라미터로만 갈린다.
   * NavLink의 `isActive`는 **search를 보지 않아** 둘 다 켜지므로 여기서 직접 대조한다.
   * 파라미터가 없는 `/board`(전체)나 상세(`/board/:id`)에서는 둘 다 꺼진다 — 상세가 어느
   * 게시판 글인지는 본문을 받아야 알 수 있고, 헤더가 그걸 기다리게 만들지 않는다.
   */
  const boardParam = new URLSearchParams(location.search).get('board')

  return (
    <>
      <header className="site-header">
        <NavLink to={isAdmin ? '/admin' : '/'} className="brand">
          해봄
          <small>{isAdmin ? '관리자' : '광명 문제해결 연결 플랫폼'}</small>
        </NavLink>
        {/* 개발 서버일 때만 뜬다 — 판단은 서버의 /health가 한다(EnvBadge 주석). */}
        <EnvBadge />
        <nav className="nav">
          {isAdmin ? (
            <AdminNav />
          ) : (
            MENU.map((m) => (
              <NavLink
                key={m.to}
                to={m.to}
                end={m.end}
                className={({ isActive }) =>
                  (m.board === undefined ? isActive : isActive && boardParam === m.board)
                    ? 'active'
                    : undefined
                }
              >
                {m.label}
              </NavLink>
            ))
          )}
        </nav>
      </header>

      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          {/* 홈은 로고가 맡고, 메뉴 첫 항목은 소개다(08-14 BI 시안). 둘은 다른 화면이다 —
              홈은 최근 카드를 보여주고 소개는 "여기가 무엇을 하는 곳인가"에 답한다. */}
          <Route path="/about" element={<AboutPage />} />
          <Route path="/problems" element={<ProblemListPage />} />
          <Route path="/problems/new" element={<ProblemNewPage />} />
          {/* 수정 링크가 가리키는 자리 — `?t=` 토큰이 유일한 본인 확인 수단이다.
              `/problems/:id`보다 **앞에** 둘 필요는 없다(경로가 한 칸 더 깊어 겹치지 않는다). */}
          <Route path="/problems/:id/edit" element={<ProblemEditPage />} />
          <Route path="/problems/:id" element={<ProblemDetailPage />} />
          <Route path="/solutions" element={<SolutionListPage />} />
          <Route path="/solutions/:id" element={<SolutionDetailPage />} />
          <Route path="/orgs/:id" element={<OrgDetailPage />} />
          <Route
            path="/map"
            element={
              <Suspense fallback={<div className="panel">지도를 불러오는 중입니다.</div>}>
                <MapPage />
              </Suspense>
            }
          />
          <Route path="/board" element={<BoardListPage />} />
          <Route path="/board/:id" element={<BoardDetailPage />} />
          {/* 사이트맵은 헤더 메뉴(08-14 BI 시안)를 건드리지 않고 푸터에서만 연다. */}
          <Route path="/sitemap" element={<SitemapPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route
            path="/admin"
            element={
              <RequireAdmin>
                <AdminReviewPage />
              </RequireAdmin>
            }
          />
          {/* `new`를 `:id`보다 먼저 둔다 — 라우터가 정적 세그먼트를 우선하지만 순서로도 드러낸다. */}
          <Route
            path="/admin/orgs"
            element={
              <RequireAdmin>
                <AdminOrgListPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/orgs/new"
            element={
              <RequireAdmin>
                <AdminOrgEditPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/orgs/:id"
            element={
              <RequireAdmin>
                <AdminOrgEditPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/cards/new"
            element={
              <RequireAdmin>
                <AdminCardEditPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/cards/:id"
            element={
              <RequireAdmin>
                <AdminCardEditPage />
              </RequireAdmin>
            }
          />
          {/* 시민문제 제목·요약 확정(017) — 해결카드와 달리 축 편집이 아니라 두 문장만 정한다. */}
          <Route
            path="/admin/problems/:id"
            element={
              <RequireAdmin>
                <AdminProblemEditPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/match"
            element={
              <RequireAdmin>
                <AdminMatchPage />
              </RequireAdmin>
            }
          />
          {/* 배점 조정(§4-라). `/admin/match/:id`와 겹치지 않도록 별도 경로에 둔다. */}
          <Route
            path="/admin/weights"
            element={
              <RequireAdmin>
                <AdminWeightPage />
              </RequireAdmin>
            }
          />
          {/* 미충족 큐(R-9). 정적 세그먼트가 `:id`보다 우선이라 경로 충돌이 없다. */}
          {/* 해결카드 기준 역조회(관리자UX N-2). `/admin/match`가 시민문제 기준이라
              그 반대쪽을 여기 둔다 — 매칭 화면 상단 링크로 들어간다. */}
          <Route
            path="/admin/match/by-card"
            element={
              <RequireAdmin>
                <AdminMatchByCardPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/match/unmet"
            element={
              <RequireAdmin>
                <AdminMatchUnmetPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/match/:id"
            element={
              <RequireAdmin>
                <AdminMatchRunPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/data"
            element={
              <RequireAdmin>
                <AdminDataPage />
              </RequireAdmin>
            }
          />
          {/* 소식은 Ghost가 맡는다(09-21) — 이 자리는 관리 화면이 아니라 안내다.
              옛 `/admin/posts/new`·`/:id`는 갈 곳이 없어 함께 걷어냈다. */}
          <Route
            path="/admin/posts"
            element={
              <RequireAdmin>
                <AdminNewsGuidePage />
              </RequireAdmin>
            }
          />
          {/* 코드·라벨 편집(09-03). 설정 하위에 두어 nav 항목을 늘리지 않는다 —
              '설정'이 end:false라 이 경로에서도 활성으로 그려진다. */}
          <Route
            path="/admin/settings/codes"
            element={
              <RequireAdmin>
                <AdminCodesPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <RequireAdmin>
                <AdminSettingsPage />
              </RequireAdmin>
            }
          />
          {/* 구축 담당자용 내부 화면 — env가 없는 환경에서는 API가 404를 주므로
              라우트가 있어도 열어봤자 오류 화면이다(존재 은폐는 서버 몫). */}
          <Route
            path="/admin/dev"
            element={
              <RequireAdmin>
                <AdminDevPage />
              </RequireAdmin>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>

      <footer className="site-footer">
        해봄 — 광명 지역 문제해결 연결 플랫폼 · 시범 운영
        {' · '}
        {/* 처리방침은 상시 노출이 법 요건이다(제30조 — 보유기간 결정 문서 §6-3). */}
        <NavLink to="/privacy" className="admin-link">
          개인정보 처리방침
        </NavLink>
        {' · '}
        {/* 관리자 화면에서도 남긴다 — 메뉴에 없는 등록·수정 화면을 찾는 통로가 여기뿐이다. */}
        <NavLink to="/sitemap" className="admin-link">
          사이트맵
        </NavLink>
        {!isAdmin && (
          <>
            {' · '}
            <NavLink to="/admin" className="admin-link">
              관리자
            </NavLink>
          </>
        )}
      </footer>

      {DevOverlay !== null && devPanelOn && (
        // fallback이 없다 — 개발 도구가 뜨는 사이에 화면에 무언가 끼어들면 안 된다.
        <Suspense fallback={null}>
          <DevOverlay />
        </Suspense>
      )}
    </>
  )
}
